import { type UseMutationResult } from '@tanstack/react-query';

import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import { createPayload, updatePayload, type PayloadSpec } from '../../../lib/forms/payload.js';
import { properties } from './endpoints.js';
import { propertyEndpoints } from './endpoints.js';
import { type CreatedProperty, type PropertyFormInput, createdProperty } from './schemas.js';

/**
 * Writing a property.
 *
 * The shape every mutation in the portal copies:
 *
 * - **An idempotency key per submission.** The Gateway retains keys for 24
 *   hours, so a double-submitted form is one property rather than two. Fresh
 *   per call — a stable key would make the second property a replay of the
 *   first.
 * - **Invalidate the lists and the details**, not a hand-listed set of pages.
 *   Over-invalidating costs a refetch; under-invalidating shows a reader a
 *   list without the thing they just created, and only one of those is
 *   visible. The reference vocabularies sit outside both prefixes on purpose:
 *   saving a property must not refetch two hundred and fifty countries.
 * - **The response is parsed.** A create that returns an unexpected shape is
 *   the same contract failure as a read that does.
 * - **Errors are not swallowed.** `ApiError.fieldErrors` reaches the caller
 *   so the form can attach each message to the field that caused it.
 */
const WRITTEN_KEYS = properties.writtenKeys;

/**
 * What the service requires, accepts and refuses.
 *
 * `code` is create-only: a property's code appears in every booking reference
 * already issued against it. `status` is update-only: the service creates every
 * property in DRAFT, so a status on the create screen would be a control that
 * silently does nothing.
 */
const PROPERTY_PAYLOAD: PayloadSpec = {
  required: ['name', 'timezone', 'currency', 'latitude', 'longitude'],
  optional: [
    'displayName',
    'description',
    'propertyType',
    'logo',
    'logoAltText',
    'countryCode',
    'stateCode',
    'cityCode',
    'email',
    'phone',
    'phoneCode',
    'mobile',
    'website',
    'abn',
    'addressLine1',
    'addressLine2',
    'addressLine3',
    'addressLine4',
    'postCode',
    'defaultCheckInTime',
    'defaultCheckOutTime',
    'pmsConfigured',
    'pmsAdapterId',
    'pmsPropertyId',
  ],
  numeric: ['starRating'],
  createOnly: ['code'],
  updateOnly: ['status'],
};

export function createPropertyPayload(values: PropertyFormInput): Record<string, unknown> {
  return createPayload(values, PROPERTY_PAYLOAD);
}

export function updatePropertyPayload(
  values: PropertyFormInput,
  version: number,
): Record<string, unknown> {
  return updatePayload(values, PROPERTY_PAYLOAD, version);
}

export function useCreateProperty(): UseMutationResult<
  CreatedProperty,
  unknown,
  PropertyFormInput
> {
  return useApiMutation<PropertyFormInput, CreatedProperty>({
    mutationFn: async (api, input) => {
      const payload = await api.post<unknown>(
        propertyEndpoints.create,
        createPropertyPayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(createdProperty, payload, propertyEndpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdatePropertyVariables {
  readonly propertyId: string;
  readonly version: number;
  readonly values: PropertyFormInput;
}

export function useUpdateProperty(): UseMutationResult<unknown, unknown, UpdatePropertyVariables> {
  return useApiMutation<UpdatePropertyVariables, unknown>({
    mutationFn: async (api, { propertyId, version, values }) => {
      // PATCH, not PUT: the service takes a partial and this form owns only
      // part of a property — its images, policies and amenities belong to
      // their own screens and must not be erased by omission here.
      await api.patch<unknown>(
        propertyEndpoints.update(propertyId),
        updatePropertyPayload(values, version),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

/**
 * Deleting a property.
 *
 * Soft, always: the service sets `deleted_at` and `deleted_by` and leaves the
 * row where it is, because reservations, payments and audit entries still
 * point at it. Nothing in this portal can hard-delete a property, and nothing
 * should — a booking whose property row has vanished cannot be honoured or
 * refunded.
 */
export function useSoftDeleteProperty(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, propertyId) => {
      await api.delete<unknown>(propertyEndpoints.delete(propertyId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
